import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PublicMedia } from "@vedaunion/shared";
import { apiFetch, apiRequest, ApiError } from "./api";

const LIST_KEY = (kind?: string) => ["media", "list", kind] as const;

/** Authenticated list of the current user's uploads (optionally filtered by kind). */
export function useMyMedia(kind?: "image" | "audio" | "pdf") {
  return useQuery<{ media: PublicMedia[] }>({
    queryKey: LIST_KEY(kind),
    queryFn: ({ signal }) =>
      apiFetch<{ media: PublicMedia[] }>(`/api/media${kind ? `?kind=${kind}` : ""}`, { signal }),
  });
}

/**
 * Upload a single file. Returns the durable PublicMedia row + URL.
 *
 * Bypasses `apiFetch` because that wrapper forces a JSON content-type and
 * stringifies the body — multipart uploads need the browser to set its own
 * `Content-Type: multipart/form-data; boundary=…`. Still goes through
 * `apiRequest` so the API origin and this build's auth (cookie on the web,
 * bearer in the app) are applied. CSRF is origin-based on the server (Hono's
 * `csrf()` middleware), which allows the same origin, the dev origin, and the
 * packaged-app origins.
 */
export function useUploadMedia() {
  const qc = useQueryClient();
  return useMutation<PublicMedia, ApiError, { file: File; alt?: string }>({
    mutationFn: async ({ file, alt }) => {
      const fd = new FormData();
      fd.append("file", file);
      if (alt) fd.append("alt", alt);
      let res: Response;
      try {
        res = await apiRequest("/api/media", { method: "POST", body: fd });
      } catch {
        throw new ApiError(0, "Network error during upload.", undefined);
      }
      let parsed: unknown;
      try {
        parsed = await res.json();
      } catch {
        parsed = undefined;
      }
      if (!res.ok) {
        const body = parsed as { message?: string; error?: string } | undefined;
        throw new ApiError(
          res.status,
          body?.message ?? body?.error ?? `Upload failed (${res.status})`,
          body,
        );
      }
      const json = parsed as { media: PublicMedia };
      return json.media;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["media"] }),
  });
}
